import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import {
  Loader2,
  ShieldCheck,
  Lock,
  AlertCircle,
  Phone,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Clock,
  CreditCard,
  CircleDashed,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const BRAND = {
  primary: "#1340A6",
  primaryHover: "#0F3590",
  accent: "#1A7DFF",
  accentHover: "#1668DB",
  surface: "#FFFFFF",
  surfaceMuted: "#F4F7FF",
  border: "#E5EAF5",
  text: "#0B1B3D",
  textMuted: "#5B6B8C",
  success: "#059669",
  successBg: "#ECFDF5",
};

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.detail || "Request failed");
  return data;
}

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n || 0));

const STAGES = { REVIEW: 0, VERIFY: 1, PAY: 2, REDIRECTING: 3 };

export default function PaymentCheckout() {
  const { linkId } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [stage, setStage] = useState(STAGES.REVIEW);
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [verifiedMobile, setVerifiedMobile] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [initiating, setInitiating] = useState(false);

  useEffect(() => {
    apiFetch(`/api/pay/${linkId}`)
      .then((data) => {
        setLink(data.link);
        if (data.error) setError(data.message || data.error);
        if (data.link && !data.link.self_amount && data.link.amount)
          setPayAmount(String(data.link.amount));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [linkId]);

  // When recipient mobile is known (wallet_topup), pre-fill the mobile box (display only)
  const isWalletTopup = !!link?.recipient_mobile_masked;

  const sendOtp = async () => {
    let payloadMobile = mobile;
    if (!isWalletTopup) {
      const clean = mobile.replace(/\D/g, "").slice(-10);
      if (clean.length !== 10) {
        toast.error("Enter a valid 10-digit mobile number");
        return;
      }
      payloadMobile = clean;
    }
    setOtpSending(true);
    try {
      const r = await apiFetch(`/api/pay/${linkId}/send-otp`, {
        method: "POST",
        body: JSON.stringify(isWalletTopup ? {} : { mobile: payloadMobile }),
      });
      setOtpSent(true);
      toast.success(
        isWalletTopup
          ? `OTP sent to the wallet owner (+91 ${r.mobile_masked || link.recipient_mobile_masked})`
          : "OTP sent to your mobile",
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length < 4) {
      toast.error("Enter the OTP");
      return;
    }
    setOtpVerifying(true);
    try {
      const data = await apiFetch(`/api/pay/${linkId}/verify-otp`, {
        method: "POST",
        body: JSON.stringify(isWalletTopup ? { otp } : { mobile, otp }),
      });
      setSessionToken(data.session_token);
      setVerifiedMobile(data.mobile || mobile);
      setStage(STAGES.PAY);
      toast.success("Mobile verified");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOtpVerifying(false);
    }
  };

  const initiatePayment = async () => {
    const amt = parseFloat(payAmount);
    if (link.self_amount && (!amt || amt <= 0)) {
      toast.error("Enter an amount");
      return;
    }
    setInitiating(true);
    try {
      const data = await apiFetch(`/api/pay/${linkId}/initiate`, {
        method: "POST",
        body: JSON.stringify({
          session_token: sessionToken,
          mobile: isWalletTopup ? undefined : verifiedMobile || mobile,
          amount: amt || null,
        }),
      });
      setStage(STAGES.REDIRECTING);
      setTimeout(() => {
        window.location.href = data.checkout_url;
      }, 900);
    } catch (e) {
      toast.error(e.message);
      setInitiating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: BRAND.primary }} />
      </div>
    );
  }

  if (error && !link) return <ErrorShell title="Payment Unavailable" message={error} />;

  return (
    <div
      data-testid="payment-checkout"
      className="w-full min-h-screen flex items-center justify-center bg-white p-4 sm:p-8 relative overflow-hidden"
      style={{ fontFamily: "Manrope, system-ui, sans-serif" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(26,125,255,0.06), transparent 60%), radial-gradient(ellipse at bottom right, rgba(19,64,166,0.05), transparent 50%)",
        }}
      />

      <div className="relative z-10 w-full max-w-5xl grid grid-cols-1 md:grid-cols-12 bg-white rounded-3xl overflow-hidden shadow-[0_24px_70px_-20px_rgba(19,64,166,0.18)] border border-[#E5EAF5]">
        <LeftPane link={link} />

        {/* RIGHT — Stacked Accordion */}
        <div className="md:col-span-7 p-6 sm:p-10 md:p-12 bg-white relative">
          <div className="absolute top-6 right-6 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: BRAND.textMuted }}>
            <Lock className="h-3 w-3" /> Secure
          </div>

          {error && link && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-4 space-y-3" data-testid="step-info">
            {/* ════════ Section 1: Review ════════ */}
            <Section
              index={1}
              title="Review payment"
              done={stage > STAGES.REVIEW}
              active={stage === STAGES.REVIEW}
              summary={
                stage > STAGES.REVIEW ? (
                  <span>
                    {link.self_amount
                      ? "Self-amount link reviewed"
                      : `₹${inr(link.amount)} · ${link.partner_name || "FlexPay"}`}
                  </span>
                ) : null
              }
            >
              <ReviewSection link={link} onProceed={() => setStage(STAGES.VERIFY)} />
            </Section>

            {/* ════════ Section 2: Verify ════════ */}
            <Section
              index={2}
              title="Verify your mobile"
              done={stage > STAGES.VERIFY}
              active={stage === STAGES.VERIFY}
              pending={stage < STAGES.VERIFY}
              summary={
                stage > STAGES.VERIFY ? (
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" style={{ color: BRAND.success }} />
                    +91 {verifiedMobile.slice(0, 2)}•••• {verifiedMobile.slice(-2)}
                  </span>
                ) : null
              }
              testId="step-mobile"
              activeTestId={otpSent ? "step-otp" : "step-mobile"}
            >
              <VerifySection
                link={link}
                isWalletTopup={isWalletTopup}
                mobile={mobile}
                setMobile={setMobile}
                otp={otp}
                setOtp={setOtp}
                otpSent={otpSent}
                setOtpSent={setOtpSent}
                otpSending={otpSending}
                otpVerifying={otpVerifying}
                sendOtp={sendOtp}
                verifyOtp={verifyOtp}
              />
            </Section>

            {/* ════════ Section 3: Pay ════════ */}
            <Section
              index={3}
              title="Confirm & pay"
              done={stage > STAGES.PAY}
              active={stage === STAGES.PAY || stage === STAGES.REDIRECTING}
              pending={stage < STAGES.PAY}
              testId="step-pay"
              activeTestId={stage === STAGES.REDIRECTING ? "step-redirecting" : "step-pay"}
              hideHeader={stage === STAGES.REDIRECTING}
            >
              {stage === STAGES.REDIRECTING ? (
                <RedirectingState gateway={link?.gateway} />
              ) : (
                <PaySection
                  link={link}
                  payAmount={payAmount}
                  setPayAmount={setPayAmount}
                  onPay={initiatePayment}
                  initiating={initiating}
                />
              )}
            </Section>
          </div>

          <TrustRow />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section accordion wrapper
// ═══════════════════════════════════════════════════════════════════════════
function Section({ index, title, done, active, pending, summary, children, testId, activeTestId, hideHeader }) {
  const status = done ? "done" : active ? "active" : "pending";
  return (
    <motion.div
      layout
      data-testid={active ? (activeTestId || testId) : testId}
      className={`rounded-2xl border transition-all ${
        active
          ? "border-[#1A7DFF] bg-white shadow-[0_8px_30px_-12px_rgba(26,125,255,0.25)]"
          : done
            ? "border-[#D9E5FB] bg-[#F4F7FF]/40"
            : "border-[#E5EAF5] bg-[#F8FAFE]/60"
      }`}
    >
      {!hideHeader && (
        <button
          type="button"
          className={`w-full px-5 py-4 flex items-center gap-3 text-left ${pending ? "cursor-not-allowed" : ""}`}
          disabled={!done}
          aria-expanded={active}
        >
          <StatusDot status={status} index={index} />
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm font-semibold ${pending ? "text-slate-400" : ""}`}
              style={{ color: pending ? undefined : BRAND.text, fontFamily: "Outfit, system-ui, sans-serif" }}
            >
              {title}
            </p>
            {done && summary && (
              <p className="text-xs mt-0.5 truncate" style={{ color: BRAND.textMuted }}>
                {summary}
              </p>
            )}
          </div>
          {done && (
            <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: BRAND.success }}>
              Done
            </span>
          )}
        </button>
      )}

      <AnimatePresence initial={false}>
        {active && (
          <motion.div
            key="content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className={`${hideHeader ? "px-5 py-6" : "px-5 pb-5 pt-1"}`}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function StatusDot({ status, index }) {
  if (status === "done") {
    return (
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: BRAND.successBg, color: BRAND.success }}
      >
        <CheckCircle2 className="h-4 w-4" />
      </div>
    );
  }
  if (status === "active") {
    return (
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
        style={{ backgroundColor: BRAND.accent, fontFamily: "Outfit, system-ui, sans-serif" }}
      >
        {index}
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 shrink-0 bg-white border border-slate-200">
      <CircleDashed className="h-4 w-4" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section 1 — Review
// ═══════════════════════════════════════════════════════════════════════════
function ReviewSection({ link, onProceed }) {
  return (
    <div className="space-y-5">
      <p className="text-sm leading-relaxed" style={{ color: BRAND.textMuted }}>
        {link.self_amount
          ? "Choose an amount you're comfortable with, verify your number, and we'll guide you through the payment."
          : link.recipient_mobile_masked
            ? `This payment will top up ${link.recipient_name || "the wallet owner"}'s wallet. We'll send a one-time code to their registered mobile to confirm.`
            : "Verify your mobile number, then complete the payment securely via your chosen method."}
      </p>

      <div className="grid grid-cols-2 gap-3">
        {link.max_amount > 0 && (
          <Stat label="Max per txn" value={`₹${inr(link.max_amount)}`} />
        )}
        {link.min_amount > 0 && (
          <Stat label="Min amount" value={`₹${inr(link.min_amount)}`} />
        )}
        {link.max_successful_payments > 1 && (
          <Stat
            label="Payments left"
            value={`${(link.max_successful_payments || 1) - (link.successful_count || 0)}`}
          />
        )}
        {link.expires_at && <Stat label="Expires" value={<Expiry expiresAt={link.expires_at} />} />}
      </div>

      <Button
        data-testid="proceed-btn"
        onClick={onProceed}
        className="w-full h-12 text-white rounded-xl text-base font-medium group transition-all"
        style={{ backgroundColor: BRAND.primary }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.primaryHover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.primary)}
      >
        Proceed to verify
        <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section 2 — Verify (mobile + inline OTP)
// ═══════════════════════════════════════════════════════════════════════════
function VerifySection({
  link,
  isWalletTopup,
  mobile,
  setMobile,
  otp,
  setOtp,
  otpSent,
  setOtpSent,
  otpSending,
  otpVerifying,
  sendOtp,
  verifyOtp,
}) {
  return (
    <div className="space-y-5">
      {isWalletTopup ? (
        // Wallet-topup: recipient mobile pre-resolved, just show it & send OTP.
        <div
          className="rounded-xl border border-[#D9E5FB] bg-[#F4F7FF]/70 p-4 flex items-start gap-3"
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: BRAND.accent + "22", color: BRAND.accent }}
          >
            <ShieldCheck className="h-4.5 w-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase" style={{ color: BRAND.textMuted }}>
              OTP will be sent to
            </p>
            <p className="text-base font-semibold mt-0.5" style={{ color: BRAND.primary, fontFamily: "Outfit, system-ui, sans-serif" }}>
              +91 {link.recipient_mobile_masked}
            </p>
            <p className="text-xs mt-1" style={{ color: BRAND.textMuted }}>
              The registered mobile of {link.recipient_name || "the wallet owner"} — only they can authorise this top-up.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-end gap-2 border-b-2 border-[#E5EAF5] focus-within:border-[#1340A6] transition-colors pb-2">
            <span className="text-2xl font-light pb-1" style={{ color: BRAND.textMuted }}>+91</span>
            <Input
              type="tel"
              maxLength={10}
              autoFocus
              readOnly={otpSent}
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && !otpSent && sendOtp()}
              placeholder="00000 00000"
              className="w-full h-12 text-2xl tracking-widest border-0 focus-visible:ring-0 px-0 placeholder:text-slate-300 bg-transparent shadow-none font-light"
              style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.text }}
              data-testid="payer-mobile"
            />
            {otpSent && (
              <button
                type="button"
                onClick={() => {
                  setOtp("");
                  setOtpSent(false);
                }}
                className="text-xs font-semibold uppercase tracking-wider mb-1 hover:underline shrink-0"
                style={{ color: BRAND.accent }}
              >
                Change
              </button>
            )}
          </div>
          <p className="text-xs mt-2.5 flex items-center gap-1.5" style={{ color: BRAND.textMuted }}>
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: BRAND.accent }} />
            We don't store your number for marketing — only for this transaction.
          </p>
        </div>
      )}

      {!otpSent && (
        <Button
          onClick={sendOtp}
          disabled={otpSending || (!isWalletTopup && mobile.length !== 10)}
          className="w-full h-12 text-white rounded-xl text-base font-medium"
          style={{ backgroundColor: BRAND.primary }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.primaryHover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.primary)}
          data-testid="send-otp-btn"
        >
          {otpSending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Phone className="h-4 w-4 mr-2" />
          )}
          Send OTP
        </Button>
      )}

      <AnimatePresence>
        {otpSent && (
          <motion.div
            key="otp-block"
            initial={{ opacity: 0, height: 0, y: -4 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              <div>
                <p
                  className="text-[11px] font-bold tracking-[0.18em] uppercase mb-2"
                  style={{ color: BRAND.accent }}
                >
                  Enter the 6-digit code
                </p>
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  autoFocus
                  data-testid="payer-otp"
                >
                  <InputOTPGroup className="gap-2 sm:gap-2.5">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="w-11 h-13 sm:w-12 sm:h-14 text-xl border border-[#E5EAF5] focus-within:border-[#1340A6] focus-within:ring-2 focus-within:ring-[#1340A6]/15 rounded-lg bg-[#F4F7FF] font-medium"
                        style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <p className="text-[11px] mt-2" style={{ color: BRAND.textMuted }}>
                  Didn't get it? It can take up to 30 seconds.
                </p>
              </div>

              <Button
                onClick={verifyOtp}
                disabled={otpVerifying || otp.length < 4}
                className="w-full h-12 text-white rounded-xl text-base font-medium"
                style={{ backgroundColor: BRAND.primary }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.primaryHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.primary)}
                data-testid="verify-otp-btn"
              >
                {otpVerifying ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-2" />
                )}
                Verify & continue
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section 3 — Pay
// ═══════════════════════════════════════════════════════════════════════════
function PaySection({ link, payAmount, setPayAmount, onPay, initiating }) {
  return (
    <div className="space-y-5">
      {link.self_amount ? (
        <SelfAmountInput payAmount={payAmount} setPayAmount={setPayAmount} link={link} />
      ) : (
        <FixedAmountSummary amount={link.amount} />
      )}

      <Button
        onClick={onPay}
        disabled={initiating}
        className="w-full h-14 text-white rounded-xl text-base font-medium transition-all flex items-center justify-center gap-2 group"
        style={{
          backgroundColor: BRAND.accent,
          boxShadow: "0 10px 30px -8px rgba(26,125,255,0.45)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.accentHover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.accent)}
        data-testid="confirm-pay-btn"
      >
        {initiating ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Processing…
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4" />
            Confirm & Pay
            {payAmount && <span className="font-semibold ml-1">₹{inr(payAmount)}</span>}
            <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </Button>
    </div>
  );
}

function RedirectingState({ gateway }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[180px] space-y-4 text-center">
      <div
        className="w-12 h-12 rounded-full animate-spin"
        style={{
          border: "4px solid #E5EAF5",
          borderTopColor: BRAND.accent,
        }}
      />
      <div>
        <p
          className="text-base font-semibold"
          style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}
        >
          Redirecting to secure payment…
        </p>
        <p className="text-xs mt-1" style={{ color: BRAND.textMuted }}>
          Please do not close this window.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Left Pane (context)
// ═══════════════════════════════════════════════════════════════════════════
function LeftPane({ link }) {
  const showAmount = link && !link.self_amount && link.amount;
  const counterShown = link?.max_successful_payments > 1;
  return (
    <div
      className="md:col-span-5 relative p-8 sm:p-10 flex flex-col justify-between border-r border-[#E5EAF5] overflow-hidden min-h-[280px] md:min-h-full text-white"
      style={{
        background: `linear-gradient(160deg, ${BRAND.primary} 0%, #0A2670 55%, ${BRAND.primary} 100%)`,
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(26,125,255,0.45), transparent 60%)",
          filter: "blur(20px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(26,125,255,0.25), transparent 60%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative z-10 flex items-center gap-2">
        <div
          className="w-10 h-10 rounded-xl bg-white flex items-center justify-center font-bold text-lg"
          style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}
        >
          F
        </div>
        <div>
          <p className="text-sm font-bold text-white" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
            FlexPay
          </p>
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-200/80">
            Hosted Checkout
          </p>
        </div>
      </div>

      <div className="relative z-10 my-6">
        <p className="text-xs font-bold tracking-[0.18em] uppercase text-blue-200/80 mb-3">
          {link?.self_amount ? "You choose" : "Amount due"}
        </p>
        {showAmount ? (
          <p
            className="text-5xl sm:text-6xl font-medium tracking-tighter text-white"
            style={{ fontFamily: "Outfit, system-ui, sans-serif" }}
          >
            <span className="text-3xl sm:text-4xl text-blue-200/70 mr-1 font-light">₹</span>
            {inr(link.amount)}
          </p>
        ) : (
          <p
            className="text-4xl sm:text-5xl font-light tracking-tighter text-blue-200/70"
            style={{ fontFamily: "Outfit, system-ui, sans-serif" }}
          >
            Flexible
          </p>
        )}
        {link?.remarks && (
          <p className="text-sm text-blue-100/80 mt-4 italic border-l-2 border-[#1A7DFF] pl-3 max-w-xs">
            "{link.remarks}"
          </p>
        )}
      </div>

      <div className="relative z-10 space-y-3">
        {link?.recipient_name && (
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 backdrop-blur-sm border border-emerald-300/30 rounded-full px-3 py-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
            <p className="text-xs font-medium text-white">Credits → {link.recipient_name}</p>
          </div>
        )}
        {link?.partner_name && !link?.recipient_name && (
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5">
            <div
              className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold"
              style={{ color: BRAND.primary }}
            >
              {link.partner_name.charAt(0).toUpperCase()}
            </div>
            <p className="text-sm font-medium text-white">{link.partner_name}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {counterShown && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-100 bg-white/10 px-2.5 py-1 rounded-md border border-white/20">
              {link.successful_count || 0} / {link.max_successful_payments} paid
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Small atoms
// ═══════════════════════════════════════════════════════════════════════════
function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E5EAF5] bg-[#F4F7FF]/60 px-4 py-3">
      <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-1" style={{ color: BRAND.textMuted }}>
        {label}
      </p>
      <p className="text-sm font-semibold" style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}>
        {value}
      </p>
    </div>
  );
}

function Expiry({ expiresAt }) {
  const text = useMemo(() => {
    try {
      const d = new Date(expiresAt);
      const diff = d.getTime() - Date.now();
      if (diff <= 0) return "expired";
      const hours = Math.floor(diff / 36e5);
      if (hours >= 24) return `${Math.floor(hours / 24)}d`;
      if (hours >= 1) return `${hours}h`;
      return `${Math.max(1, Math.floor(diff / 6e4))}m`;
    } catch {
      return "soon";
    }
  }, [expiresAt]);
  return (
    <span className="inline-flex items-center gap-1">
      <Clock className="h-3 w-3" /> {text}
    </span>
  );
}

function FixedAmountSummary({ amount }) {
  return (
    <div className="rounded-2xl border border-[#E5EAF5] bg-[#F4F7FF]/60 px-6 py-6 text-center">
      <p className="text-[10px] font-bold tracking-[0.18em] uppercase mb-2" style={{ color: BRAND.textMuted }}>
        You will pay
      </p>
      <p
        className="text-4xl sm:text-5xl font-medium tracking-tighter"
        style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}
      >
        <span className="text-xl sm:text-2xl mr-1 font-light" style={{ color: BRAND.textMuted }}>₹</span>
        {inr(amount)}
      </p>
    </div>
  );
}

function SelfAmountInput({ payAmount, setPayAmount, link }) {
  return (
    <div className="space-y-4">
      <div
        className="rounded-2xl border-2 border-[#E5EAF5] focus-within:border-[#1A7DFF] transition-colors px-6 py-6 text-center bg-[#F4F7FF]/50"
      >
        <p className="text-[10px] font-bold tracking-[0.18em] uppercase mb-2" style={{ color: BRAND.textMuted }}>
          Your amount
        </p>
        <div className="flex items-center justify-center gap-1">
          <span className="text-2xl sm:text-3xl font-light" style={{ color: BRAND.textMuted }}>₹</span>
          <input
            type="number"
            inputMode="decimal"
            min={link.min_amount || 1}
            max={link.max_amount || undefined}
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            placeholder="0"
            className="w-full text-4xl sm:text-5xl font-medium tracking-tighter placeholder:text-slate-300 bg-transparent text-center focus:outline-none"
            style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}
            data-testid="pay-amount"
          />
        </div>
      </div>
      {link.suggested_amounts && link.suggested_amounts.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center">
          {link.suggested_amounts.map((a) => {
            const active = String(payAmount) === String(a);
            return (
              <button
                key={a}
                type="button"
                onClick={() => setPayAmount(String(a))}
                className="px-4 py-1.5 rounded-full border text-sm font-medium transition-all"
                style={
                  active
                    ? {
                        borderColor: BRAND.accent,
                        backgroundColor: BRAND.accent,
                        color: "white",
                        boxShadow: "0 4px 14px -4px rgba(26,125,255,0.5)",
                      }
                    : { borderColor: "#E5EAF5", color: BRAND.textMuted, backgroundColor: "white" }
                }
                data-testid={`suggest-${a}`}
              >
                ₹{inr(a)}
              </button>
            );
          })}
        </div>
      )}
      {(link.min_amount > 0 || link.max_amount > 0) && (
        <p className="text-xs text-center" style={{ color: BRAND.textMuted }}>
          {link.min_amount > 0 && `Min ₹${inr(link.min_amount)}`}
          {link.min_amount > 0 && link.max_amount > 0 && " · "}
          {link.max_amount > 0 && `Max ₹${inr(link.max_amount)}`}
        </p>
      )}
    </div>
  );
}

function TrustRow() {
  return (
    <div className="flex items-center justify-center gap-5 pt-5 mt-5 border-t border-[#E5EAF5]">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: BRAND.textMuted }}>
        <Lock className="h-3 w-3" /> 256-bit encrypted
      </span>
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: BRAND.textMuted }}>
        <ShieldCheck className="h-3 w-3" /> OTP verified
      </span>
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: BRAND.textMuted }}>
        <Sparkles className="h-3 w-3" /> FlexPay
      </span>
    </div>
  );
}

function ErrorShell({ title, message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4" style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
      <div className="max-w-md w-full bg-white rounded-3xl shadow-[0_24px_60px_-20px_rgba(19,64,166,0.15)] border border-[#E5EAF5] p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
          <AlertCircle className="h-8 w-8 text-red-500" />
        </div>
        <h1
          className="text-2xl font-medium mb-2"
          style={{ fontFamily: "Outfit, system-ui, sans-serif", color: BRAND.primary }}
        >
          {title}
        </h1>
        <p style={{ color: BRAND.textMuted }}>{message}</p>
      </div>
    </div>
  );
}
